import manifest from "@/content/campaigns/medspa-workflow-check-01.json";
import {
  isTouch,
  UUID,
  type CampaignTouch,
} from "../../../supabase/functions/_shared/marketing-contract";

export const CAMPAIGN_ENABLED =
  import.meta.env.VITE_CAMPAIGN_FUNNEL_ENABLED === "true";
export const CONTEXT_KEY = "adiastra_campaign_context_v1";
export const CONTEXT_TTL = 24 * 60 * 60 * 1000;
type Context = {
  visit_id: string;
  first_touch: CampaignTouch | null;
  last_touch: CampaignTouch | null;
  captured_at: number;
  expires_at: number;
  reason: "allowlisted" | "direct_or_unknown" | "invalid_campaign";
  storage_degraded: boolean;
};
export type SessionStore = Pick<Storage, "getItem" | "setItem" | "removeItem">;
export function browserSession(): SessionStore | undefined {
  try {
    return typeof window === "undefined" ? undefined : window.sessionStorage;
  } catch {
    return undefined;
  }
}
export class CampaignContext {
  private current: Context | null = null;
  constructor(
    private storage?: SessionStore,
    private now = Date.now,
  ) {}
  read(): Context | null {
    if (!this.current) {
      try {
        const raw = this.storage?.getItem(CONTEXT_KEY);
        const v = raw && raw.length < 4096 ? JSON.parse(raw) : null;
        if (
          v &&
          UUID.test(v.visit_id) &&
          typeof v.captured_at === "number" &&
          typeof v.expires_at === "number" &&
          v.expires_at === v.captured_at + CONTEXT_TTL &&
          ["allowlisted", "direct_or_unknown", "invalid_campaign"].includes(
            v.reason,
          ) &&
          (v.first_touch === null || isTouch(v.first_touch)) &&
          (v.last_touch === null || isTouch(v.last_touch))
        ) {
          // Reconstruct only known fields; never preserve arbitrary stored data.
          this.current = {
            visit_id: v.visit_id,
            first_touch: v.first_touch,
            last_touch: v.last_touch,
            captured_at: v.captured_at,
            expires_at: v.expires_at,
            reason: v.reason,
            storage_degraded: false,
          };
        }
      } catch {
        /* Storage is optional. */
      }
    }
    if (
      this.current &&
      (this.current.expires_at <= this.now() ||
        this.current.captured_at > this.now())
    ) {
      this.current = null;
      try {
        this.storage?.removeItem(CONTEXT_KEY);
      } catch {
        /* optional */
      }
    }
    return this.current;
  }
  capture(path: string, search: string): Context {
    const previous = this.read();
    const params = new URLSearchParams(search);
    const explicit = [
      "campaign",
      "campaign_rev",
      "creative",
      "creative_rev",
    ].some((k) => params.has(k));
    if (previous && !explicit) return previous;
    const valid =
      explicit &&
      path === manifest.landing_path &&
      this.now() < Date.parse(manifest.valid_until) &&
      params.get("campaign") === manifest.campaign_id &&
      params.get("campaign_rev") === String(manifest.revision) &&
      params.get("creative") === manifest.creative_id &&
      params.get("creative_rev") === String(manifest.creative_revision) &&
      ["campaign", "campaign_rev", "creative", "creative_rev"].every(
        (k) => params.getAll(k).length === 1,
      );
    const source = params.get("utm_source") ?? "direct";
    const medium = params.get("utm_medium") ?? "none";
    const touch: CampaignTouch | null = valid
      ? {
          campaign_id: manifest.campaign_id,
          campaign_revision: manifest.revision,
          creative_id: manifest.creative_id,
          creative_revision: manifest.creative_revision,
          manifest_hash: manifest.manifest_hash,
          landing_path: manifest.landing_path,
          source:
            source === "linkedin" ||
            source === "direct" ||
            source === "research"
              ? source
              : "other",
          medium:
            medium === "organic_social" ||
            medium === "none" ||
            medium === "moderated"
              ? medium
              : "other",
        }
      : null;
    const at = previous?.captured_at ?? this.now();
    this.current = {
      visit_id: previous?.visit_id ?? crypto.randomUUID(),
      first_touch: previous?.first_touch ?? touch,
      last_touch: touch,
      captured_at: at,
      expires_at: at + CONTEXT_TTL,
      reason: valid
        ? "allowlisted"
        : explicit
          ? "invalid_campaign"
          : "direct_or_unknown",
      storage_degraded: !this.storage,
    };
    try {
      this.storage?.setItem(CONTEXT_KEY, JSON.stringify(this.current));
    } catch {
      this.current.storage_degraded = true;
    }
    return this.current;
  }
}
export const campaignContext = new CampaignContext(browserSession());
export function captureCampaignLocation(path: string, search: string) {
  if (!CAMPAIGN_ENABLED) return;
  try {
    campaignContext.capture(path, search);
  } catch {
    /* Attribution never gates the app. */
  }
}
